import React from "react";
import { Loader2 } from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import { useQuery } from "@tanstack/react-query";
import { axiosPrivate } from "@/api/axios";
import { fetchAllTickets } from "@/features/tickets/api/ticketsApi";
import { Ticket } from "@/features/tickets/types";
import { createTicketColumns } from "@/features/tickets/components/ticketColumns";
import ReplyTicketDialog from "@/features/tickets/components/ReplyTicketDialog";
import { User } from "@/lib/columns";

export default function TicketsPage() {
  const [replyOpen, setReplyOpen] = React.useState(false);
  const [selectedTicket, setSelectedTicket] = React.useState<Ticket | null>(null);

  const { data: tickets = [], isLoading } = useQuery<Ticket[]>({
    queryKey: ["tickets"],
    queryFn: fetchAllTickets,
    staleTime: 1000 * 60 * 2,
  });

  const { data: usersResponse } = useQuery({
    queryKey: ["users-by-ticket-ids", tickets.map((t) => t.user_id).sort().join(",")],
    enabled: tickets.length > 0,
    queryFn: async () => {
      const uniqueIds = [...new Set(tickets.map((t) => t.user_id))];
      const results = await Promise.all(
        uniqueIds.map((id) =>
          axiosPrivate.get(`/users/${id}`).then((r) => r.data?.data ?? r.data).catch(() => null)
        )
      );
      return results.filter(Boolean) as User[];
    },
    staleTime: 1000 * 60 * 5,
  });

  const userMap = React.useMemo(() => {
    const map = new Map<number, string>();
    for (const u of usersResponse ?? []) {
      const displayName = u.member_name ?? u.username ?? u.email ?? null;
      if (u.id && displayName) map.set(u.id, displayName);
    }
    return map;
  }, [usersResponse]);

  const handleReply = React.useCallback((ticket: Ticket) => {
    setSelectedTicket(ticket);
    setReplyOpen(true);
  }, []);

  const columns = React.useMemo(
    () => createTicketColumns({ onReply: handleReply, userMap }),
    [handleReply, userMap]
  );

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Support Tickets</h1>
      <p className="text-muted-foreground">View and respond to user support tickets.</p>

      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={tickets}
          placeholder="Filter by subject"
          filterColumns={["subject"]}
        />
      )}

      <ReplyTicketDialog
        open={replyOpen}
        onOpenChange={setReplyOpen}
        ticket={selectedTicket}
        userMap={userMap}
      />
    </div>
  );
}
